import { useCallback, useEffect, useMemo, useRef } from "react"
import { Feature } from "@turf/helpers"
import Fuse from "fuse.js"

import {
  createSearchConfig,
  createSearchPattern,
  filterSearchResult,
} from "providers/venue/utils/search"
import { ISearchConfig } from "services/venue"
import { IFeature } from "interfaces"

const DEFAULT_SEARCH_CONFIG = {
  keys: [
    {
      name: "name",
    },
    {
      name: "category",
    },
    {
      name: "groups",
    },
    {
      name: "local_categories",
    },
    {
      name: "properties.keywords",
    },
    {
      name: "feature_type",
    },
    {
      name: "kiosk.name",
    },
    {
      name: "unit.name",
    },
    {
      name: "section.name",
    },
  ],
  featureTypes: ["=amenity", "=occupant | =amenity | =section"],
}
export type Search = (value: string, venueFilter: string) => Feature[]
export interface IUseFeatureSearchProps {
  config: ISearchConfig
  collections: IFeature[]
  enabled?: boolean
}
export interface IUseFeatureSearchResult {
  fuse: Fuse<any>
  search: Search
}
export type UseFeatureSearchFn = (
  props: IUseFeatureSearchProps
) => IUseFeatureSearchResult

// Switching languages might inject invisible characters like Zero Width Non-Joiners (\u200C) or directionality marks (\u200E, \u200F)
const sanitizeInput = (str: string) =>
  str
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "")
    .normalize("NFC")
    .trim()

export const useFeatureSearch: UseFeatureSearchFn = ({
  config,
  collections = [],
  enabled,
}) => {
  const fuseInstance = useRef<Fuse<any> | null>(null)
  const searchConfig = useMemo(() => {
    try {
      if (enabled) return createSearchConfig(config)
    } catch (error) {
      return createSearchConfig(DEFAULT_SEARCH_CONFIG)
    }
  }, [config, enabled])

  const search = useCallback(
    (value, venueFilter: string) => {
      let result = []
      const sanitizedValue = sanitizeInput(value)

      if (!sanitizedValue || !fuseInstance.current) return result

      const searchPatterns = createSearchPattern(sanitizedValue)

      for (const logicName of searchConfig.featureTypes) {
        const searchLogic = searchPatterns[logicName]
        if (!searchLogic) continue

        const matched = fuseInstance.current.search(searchLogic)
        if (matched.length > 0) {
          result = matched
            .map((result) => result.item)
            .filter(filterSearchResult)
            .filter(
              (result) =>
                !venueFilter || result.properties?.venue_id === venueFilter
            )
          break
        }
      }
      return result
    },
    [fuseInstance, searchConfig]
  )

  /** Effects */
  // Config Fuse.js instance
  useEffect(() => {
    if (enabled && searchConfig && !fuseInstance.current) {
      fuseInstance.current = new Fuse([], {
        useExtendedSearch: true,
        threshold: 0.2,
        keys: searchConfig.keys,
      })
    }
  }, [searchConfig, enabled])

  // Set data collection to the Fuse instance
  useEffect(() => {
    if (fuseInstance.current && collections && collections.length > 0) {
      fuseInstance.current.setCollection(collections)
    }
  }, [fuseInstance, collections])

  return {
    fuse: fuseInstance.current,
    search,
  }
}
